import { useState, useEffect, useRef, FC } from "react";
import { createPortal } from "react-dom";
import {
  ImageModalContent,
  ImageModalDiv,
  ImageModalImg,
  StyledItemSubmissionImg,
  ImageContainer,
  OverlayText,
  Spinner,
  SpinnerWrapper,
} from "../../pages/MyItems/ItemSubmission.styled";
import { ReactComponent as NoImageIcon } from "../../assets/images/No_Image_Icon.svg";

interface ImageModalProps {
  src: string | undefined;
  alt?: string;
  quantity?: string | null;
}

const ImageModal: FC<ImageModalProps> = ({
  src,
  alt = "Image",
  quantity = null,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isValidImage, setIsValidImage] = useState<boolean>(true);
  const [displaySrc, setDisplaySrc] = useState<string | undefined>(undefined);
  const [largeSrc, setLargeSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLargeImage, setIsLoadingLargeImage] = useState(false);

  // Zoom state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  // Use refs for smooth dragging and zooming
  const scaleRef = useRef(1);
  const translateXRef = useRef(0);
  const translateYRef = useRef(0);
  const imageElementRef = useRef<HTMLImageElement | null>(null);
  const dragState = useRef({
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    isDragging: false,
  });

  const parsedQuantity = quantity !== null ? parseInt(quantity, 10) : null;
  const displayQuantity =
    isNaN(parsedQuantity as number) || parsedQuantity === null
      ? null
      : parsedQuantity > 1000
      ? ">1000"
      : parsedQuantity;

  useEffect(() => {
    if (!src || src == "null_mainimg.jpeg") {
      setIsValidImage(false);
      setDisplaySrc(undefined);
      setIsLoading(false);
      return;
    }

    // Data URLs are self-contained — display immediately without a network probe
    if (src.startsWith("data:")) {
      setIsValidImage(true);
      setDisplaySrc(src);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const img = new Image();
    img.src = src;

    img.onload = () => {
      setIsValidImage(true);
      setDisplaySrc(src);
      setIsLoading(false);
    };
    img.onerror = () => {
      setIsValidImage(false);
      setDisplaySrc(undefined);
      setIsLoading(false);
    };
    setIsLoading(true);
  }, [src]);

  // Load large image when modal opens
  useEffect(() => {
    if (isOpen && src && !src.startsWith('blob:') && !src.startsWith('data:')) {
      // Try to load large image from S3
      const largeImageUrl = src.replace('_mainimg.jpeg', '_largeimg.jpeg');

      setIsLoadingLargeImage(true);
      const img = new Image();
      img.src = largeImageUrl;

      img.onload = () => {
        setLargeSrc(largeImageUrl);
        setIsLoadingLargeImage(false);
        console.log('Large image loaded successfully from S3');
      };

      img.onerror = () => {
        // If large image fails to load, fall back to the main image
        setLargeSrc(null);
        setIsLoadingLargeImage(false);
        console.log('Large image not available, using main image');
      };
    } else if (!isOpen) {
      // Reset large image when modal closes
      setLargeSrc(null);
    }
  }, [isOpen, src]);

  // Reset zoom when modal closes
  useEffect(() => {
    if (!isOpen) {
      setScale(1);
      scaleRef.current = 1;
      translateXRef.current = 0;
      translateYRef.current = 0;
      setTranslateX(0);
      setTranslateY(0);
      setIsPanning(false);
    }
  }, [isOpen]);

  // Keep refs in sync with state
  useEffect(() => {
    scaleRef.current = scale;
    translateXRef.current = translateX;
    translateYRef.current = translateY;
  }, [scale, translateX, translateY]);

  // Handle pinch-to-zoom, panning, and double-tap on mobile
  useEffect(() => {
    if (!isOpen) return;

    let lastTouchDistance = 0;
    let isPinching = false;
    let lastTapTime = 0;

    const applyTransform = () => {
      if (imageElementRef.current) {
        imageElementRef.current.style.transform =
          `translate(${translateXRef.current}px, ${translateYRef.current}px) scale(${scaleRef.current})`;
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      // Prevent default browser zoom
      if (e.touches.length > 1) {
        e.preventDefault();
      }

      if (e.touches.length === 2) {
        isPinching = true;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastTouchDistance = Math.sqrt(dx * dx + dy * dy);
      } else if (e.touches.length === 1) {
        // Handle double-tap to zoom
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapTime;

        if (tapLength < 300 && tapLength > 0) {
          // Double tap detected
          e.preventDefault();
          if (scaleRef.current === 1) {
            scaleRef.current = 2.5;
            setScale(2.5);
          } else {
            scaleRef.current = 1;
            translateXRef.current = 0;
            translateYRef.current = 0;
            setScale(1);
            setTranslateX(0);
            setTranslateY(0);
          }
          applyTransform();
        } else if (scaleRef.current > 1) {
          // Start panning if zoomed
          dragState.current.isDragging = true;
          dragState.current.startX = e.touches[0].clientX;
          dragState.current.startY = e.touches[0].clientY;
          dragState.current.lastX = translateXRef.current;
          dragState.current.lastY = translateYRef.current;
          setIsPanning(true);
        }
        lastTapTime = currentTime;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && isPinching) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const touchDistance = Math.sqrt(dx * dx + dy * dy);

        const scaleChange = touchDistance / lastTouchDistance;
        const newScale = Math.max(1, Math.min(5, scaleRef.current * scaleChange));

        scaleRef.current = newScale;
        lastTouchDistance = touchDistance;

        // Apply transform directly to DOM for smooth feedback
        applyTransform();
      } else if (e.touches.length === 1 && dragState.current.isDragging) {
        e.preventDefault();
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;

        const deltaX = currentX - dragState.current.startX;
        const deltaY = currentY - dragState.current.startY;

        translateXRef.current = dragState.current.lastX + deltaX;
        translateYRef.current = dragState.current.lastY + deltaY;

        // Apply transform directly to DOM for smooth feedback
        applyTransform();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        isPinching = false;
        dragState.current.isDragging = false;
        setIsPanning(false);

        // Update state with final values
        setScale(scaleRef.current);
        setTranslateX(translateXRef.current);
        setTranslateY(translateYRef.current);
      }
    };

    const imageElement = document.querySelector('[data-zoomable="true"]') as HTMLElement;
    if (imageElement) {
      imageElement.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
      imageElement.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
      imageElement.addEventListener('touchend', handleTouchEnd as EventListener);
    }

    return () => {
      if (imageElement) {
        imageElement.removeEventListener('touchstart', handleTouchStart as EventListener);
        imageElement.removeEventListener('touchmove', handleTouchMove as EventListener);
        imageElement.removeEventListener('touchend', handleTouchEnd as EventListener);
      }
    };
  }, [isOpen]); // Only re-run when modal opens/closes, not on every state change

  // Handle mouse wheel zoom and mouse drag panning on desktop
  useEffect(() => {
    if (!isOpen) return;

    const applyTransform = () => {
      if (imageElementRef.current) {
        imageElementRef.current.style.transform =
          `translate(${translateXRef.current}px, ${translateYRef.current}px) scale(${scaleRef.current})`;
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const newScale = Math.max(1, Math.min(5, scaleRef.current * zoomFactor));

      // If zooming back to 1, reset position
      if (newScale === 1) {
        translateXRef.current = 0;
        translateYRef.current = 0;
        setTranslateX(0);
        setTranslateY(0);
      }

      scaleRef.current = newScale;
      setScale(newScale);
      applyTransform();
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (scaleRef.current > 1) {
        e.preventDefault();
        dragState.current.isDragging = true;
        dragState.current.startX = e.clientX;
        dragState.current.startY = e.clientY;
        dragState.current.lastX = translateXRef.current;
        dragState.current.lastY = translateYRef.current;
        setIsPanning(true);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragState.current.isDragging) return;
      e.preventDefault();

      const deltaX = e.clientX - dragState.current.startX;
      const deltaY = e.clientY - dragState.current.startY;

      translateXRef.current = dragState.current.lastX + deltaX;
      translateYRef.current = dragState.current.lastY + deltaY;
      applyTransform();
    };

    const handleMouseUp = () => {
      if (dragState.current.isDragging) {
        dragState.current.isDragging = false;
        setIsPanning(false);
        setTranslateX(translateXRef.current);
        setTranslateY(translateYRef.current);
      }
    };

    const imageElement = document.querySelector('[data-zoomable="true"]') as HTMLElement;
    const modalElement = imageElement?.closest('[data-scroll-lock-scrollable]') as HTMLElement;

    if (imageElement) {
      imageElement.addEventListener('wheel', handleWheel, { passive: false });
      imageElement.addEventListener('mousedown', handleMouseDown);
    }
    // Listen on window so drag continues even if cursor leaves the image
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Also capture wheel on the modal container to prevent page scroll
    if (modalElement) {
      modalElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (imageElement) {
        imageElement.removeEventListener('wheel', handleWheel);
        imageElement.removeEventListener('mousedown', handleMouseDown);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (modalElement) {
        modalElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen]);

  // Toggle isOpen to open and close larger image
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Disable body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      window.addEventListener("keydown", handleKeyDown);
    } else {
      // Re-enable body scrolling when modal is closed
      document.body.style.overflow = 'unset';
      window.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      // Cleanup: re-enable scrolling when component unmounts
      document.body.style.overflow = 'unset';
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      {isLoading && (
        <ImageContainer>
          <NoImageIcon style={{ width: "200px", maxWidth: "100%", maxHeight: "250px", borderRadius: "10%" }} />
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </ImageContainer>
      )}
      {!isOpen && !isLoading && (
        <ImageContainer>
          {isValidImage && displaySrc ? (
            <StyledItemSubmissionImg
              src={displaySrc}
              onClick={() => setIsOpen(true)}
            />
          ) : (
            <NoImageIcon style={{ width: "200px", maxWidth: "100%", maxHeight: "250px", borderRadius: "10%" }} />
          )}
          {displayQuantity != null && (
            <OverlayText>{displayQuantity}</OverlayText>
          )}
        </ImageContainer>
      )}
      {isOpen && !isLoading && createPortal(
        <ImageModalDiv>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              fontSize: '24px',
              fontWeight: 'bold',
              cursor: 'pointer',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            aria-label="Close"
          >
            ✕
          </button>
          <ImageModalContent onClick={(e) => e.stopPropagation()}>
            {isLoadingLargeImage && (
              <SpinnerWrapper>
                <Spinner />
              </SpinnerWrapper>
            )}
            <ImageModalImg
              ref={(el) => { imageElementRef.current = el; }}
              src={largeSrc || displaySrc}
              style={{
                opacity: isLoadingLargeImage ? 0.5 : 1,
                transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                transformOrigin: 'center center',
                transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                cursor: scale > 1 ? 'grab' : 'default',
              }}
            />
          </ImageModalContent>
        </ImageModalDiv>,
        document.body
      )}
    </>
  );
};

export default ImageModal;
